/**
 * Descarga de un blob devuelto por la API (Excel, PDF).
 *
 * Extraído de `AnalyticsCompleto.jsx`, donde vivía como helper local, cuando
 * el tablero necesitó lo mismo. Lo consumen `Analytics.jsx`,
 * `AnalyticsCompleto.jsx` y `VentaAnimales.jsx`.
 *
 * Depende de `Access-Control-Expose-Headers: Content-Disposition` en el
 * backend (app/__init__.py): sin eso el navegador no deja leer el header y el
 * archivo baja siempre con el nombre de fallback.
 *
 * Los otros exports del panel (`CargaMasiva.jsx`, `MisClientes.jsx`,
 * `EventoDetalle.jsx`) arman el nombre a mano y NO leen el header, así que no
 * son el mismo caso — migrarlos no arregla nada.
 */

/**
 * Nombre del archivo según el header `Content-Disposition`, o null.
 *
 * Se prioriza `filename*` (RFC 5987), que es el que trae los caracteres no
 * ASCII correctamente codificados: cuando el nombre lleva un acento o una eñe,
 * Werkzeug emite los DOS (`filename=...; filename*=UTF-8''...`) y el simple
 * viene degradado.
 *
 * El corte en `;` no es cosmético: un patrón como `[^"]+` sobre un header sin
 * comillas se come el resto de la línea, y el archivo termina bajando con los
 * dos parámetros pegados como nombre.
 *
 * Hoy ningún caller llega a la rama de `filename*`: los cuatro arman el nombre
 * desde `evento.slug`, y `Evento._slugify` normaliza a ASCII (`ñ` → `n`), así
 * que Werkzeug emite sólo el `filename` simple. Se soporta igual porque el día
 * que un `download_name` salga de un `nombre_evento` crudo, Werkzeug manda los
 * dos y el simple viene degradado.
 */
export function nombreDeContentDisposition(header) {
  const cd = header || '';

  const rfc5987 = /filename\*=\s*UTF-8''([^;]+)/i.exec(cd);
  if (rfc5987) {
    try {
      return decodeURIComponent(rfc5987[1].trim());
    } catch {
      // Porcentajes mal formados: mejor el nombre crudo que romper la descarga.
      return rfc5987[1].trim();
    }
  }

  const simple = /filename=\s*"([^"]+)"|filename=\s*([^;]+)/i.exec(cd);
  if (simple) {
    return (simple[1] || simple[2] || '').trim() || null;
  }

  return null;
}

/**
 * Dispara la descarga de la respuesta (axios con `responseType: 'blob'`).
 *
 * @param {object} res            respuesta de axios
 * @param {string} nombreFallback nombre a usar si el header no trae uno
 */
export function descargarBlob(res, nombreFallback) {
  // `res.data` YA es un Blob (axios con responseType: 'blob'). Re-envolverlo en
  // `new Blob([...])` le borra el MIME y queda type:"" — la descarga anda igual
  // por `link.download`, pero cualquier preview pierde el tipo.
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombreDeContentDisposition(res.headers?.['content-disposition']) || nombreFallback;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke DIFERIDO, como en GenerarPDF.jsx: revocar en el mismo tick puede
  // matar la descarga a mitad de camino. Con un Excel chico el navegador
  // alcanza a quedárselo y no se nota, pero este es el helper que van a usar
  // todos — el próximo export grande que se migre acá heredaría el patrón que
  // GenerarPDF ya descartó, y ahí sí se pierden los minutos de generación.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
